/**
 * Topology API (brief §5 Phase 3): pairing confirmation queue, extension
 * pairs, DID management, and dial-plan rulesets.
 *
 * The pairing queue is the §4D confirmation screen contract: confident
 * matches are *proposed*, an operator confirms (optionally swapping
 * primary/backup) or denies, and a denial is remembered server-side —
 * a decided pair is never re-proposed.
 */

import { apiFetch } from "./client";

/** A discovered account (inventory / unpaired surfacing). */
export interface DiscoveredAccountRow {
  id: number;
  source_node_id: number;
  domain: string;
  account_name: string;
  usertype: string | null;
  display_name: string | null;
  ani: string[] | null;
  eani: string[] | null;
  email: string | null;
  building: string | null;
  room: string | null;
  department: string | null;
  disabled: boolean;
  last_seen: string | null;
}

/** One pairing suggestion (proposed / confirmed / denied). */
export interface PairingSuggestion {
  id: number;
  primary_node_id: number;
  primary_ext: string;
  backup_node_id: number;
  backup_ext: string;
  matched_on: string | null;
  status: "proposed" | "confirmed" | "denied";
  pair_id: number | null;
  decided_by: string | null;
  decided_at: string | null;
}

/** An extension pair and its reconciliation state. */
export interface ExtensionPairRow {
  id: number;
  user_id: number;
  primary_node_id: number;
  primary_ext: string;
  backup_node_id: number;
  backup_ext: string;
  publicized_number: string | null;
  state: string;
  last_verified: string | null;
}

/** A DID with carrier/location rendered as names. */
export interface DidRow {
  id: number;
  number: string;
  pair_id: number | null;
  node_hint_id: number | null;
  carrier: string | null;
  e911_location: string | null;
  status: string;
  notes: string | null;
  carrier_confirmed: boolean;
  carrier_confirmed_by: string | null;
  e911_confirmed: boolean;
  e911_confirmed_by: string | null;
  /** Which confirmations are still outstanding (warnings derive from this). */
  needs: { carrier: boolean; e911: boolean };
}

/** Record (or clear) the operator's carrier/E911 attestation for a DID. */
export function confirmDid(
  didId: number,
  body: { carrier?: boolean; e911?: boolean },
): Promise<{ id: number; carrier_confirmed: boolean; e911_confirmed: boolean }> {
  return apiFetch(`/api/sync/dids/${didId}/confirm`, { method: "POST", body });
}

/** A recorded per-node dial-plan ruleset and its drift state. */
export interface RulesetRow {
  node_id: number;
  convention_id: number;
  rule_count: number;
  last_pushed: string | null;
  drift_state: string | null;
}

/** The DID CSV import report (dry-run and commit share this shape). */
export interface DidImportReport {
  ok: boolean;
  created: number;
  skipped: number;
  problems: string[];
  [key: string]: unknown;
}

// --- inventory -----------------------------------------------------------

/** How current one node's slice of the discovered-account inventory is. */
export interface InventoryStatusRow {
  node_id: number;
  node: string;
  accounts: number;
  /** ISO timestamp, or null when the node has never imported. */
  last_import: string | null;
}

export function getInventoryStatus(): Promise<{
  items: InventoryStatusRow[];
  total: number;
}> {
  return apiFetch("/api/sync/inventory/status");
}

/** Re-import every node's accounts now; returns per-pass totals. */
export function refreshInventory(): Promise<Record<string, number>> {
  return apiFetch("/api/sync/inventory/refresh", { method: "POST" });
}

// --- pairing -------------------------------------------------------------

export function getSuggestions(
  status?: string,
): Promise<{ items: PairingSuggestion[]; total: number }> {
  const qs = status ? `?status_filter=${encodeURIComponent(status)}` : "";
  return apiFetch(`/api/sync/pairing/suggestions${qs}`);
}

export function refreshPairing(): Promise<Record<string, number>> {
  return apiFetch("/api/sync/pairing/refresh", { method: "POST" });
}

export function confirmSuggestion(id: number, swap = false): Promise<PairingSuggestion> {
  return apiFetch(`/api/sync/pairing/suggestions/${id}/confirm`, {
    method: "POST",
    body: { swap },
  });
}

export function denySuggestion(id: number): Promise<PairingSuggestion> {
  return apiFetch(`/api/sync/pairing/suggestions/${id}/deny`, { method: "POST" });
}

export function getUnpaired(): Promise<{ items: DiscoveredAccountRow[]; total: number }> {
  return apiFetch("/api/sync/unpaired");
}

// --- change feed: activity + drift (ADR 0030) ----------------------------

/** One PBX config change from the ingested audit feed. */
export interface ActivityRow {
  domain: string;
  at: string;
  who: string | null;
  src_ip: string | null;
  ext: string | null;
  field: string | null;
  old: string | null;
  new: string | null;
  msg: string;
}

export function getPbxActivity(params: {
  domain?: string;
  ext?: string;
  limit?: number;
}): Promise<{ items: ActivityRow[]; count: number }> {
  const qs = new URLSearchParams();
  if (params.domain) qs.set("domain", params.domain);
  if (params.ext) qs.set("ext", params.ext);
  if (params.limit) qs.set("limit", String(params.limit));
  const q = qs.toString();
  return apiFetch(`/api/sync/activity${q ? `?${q}` : ""}`);
}

/** One field change that triggered a drift flag. */
export interface DriftChange {
  ext: string;
  field: string;
  who: string | null;
  old: string | null;
  new: string | null;
  at: string;
}

/** A pair flagged for review: which endpoint(s) drifted and how it was found. */
export interface DriftFlag {
  pair_id: number;
  primary: string;
  backup: string;
  since: string | null;
  primary_changes: DriftChange[];
  backup_changes: DriftChange[];
  sources: string[];
  sampled_fields: string[];
  sampled_at: string | null;
}

export function getDriftFlags(): Promise<{ items: DriftFlag[]; total: number }> {
  return apiFetch("/api/sync/drift/flags");
}

/** Live per-field diff of one pair's backup vs its primary (on-click detail). */
export interface CloneDriftResult {
  status: "in_sync" | "drifted" | "unknown";
  drift: Record<string, { expected: unknown; actual: unknown }>;
}

export function getPairCloneDrift(pairId: number): Promise<CloneDriftResult> {
  return apiFetch(`/api/topology/pairs/${pairId}/clone-drift`);
}

// --- staged dial-plan changes (ADR 0032) ---------------------------------

/** One target of a staged routing change. */
export interface ChangeTarget {
  kind: "plan" | "default" | "trunk" | "plan-delete";
  id: string | null;
  name: string;
  global: boolean;
  rights_ok?: boolean;
  blocked?: string;
}

/** One dps entry (pattern → replacement over a trunk). */
export interface DpsEntry {
  pattern: string;
  replacement: string;
  trunk: string;
  preference: string;
}

/** A staged routing change-set with its per-target diff and results. */
export interface ChangeSetRow {
  id: number;
  node_id: number;
  domain: string;
  status:
    | "draft"
    | "approved"
    | "scheduled"
    | "applied"
    | "failed"
    | "rolled_back"
    | "cancelled";
  targets: ChangeTarget[];
  diff: Record<
    string,
    {
      add?: DpsEntry[];
      remove?: DpsEntry[];
      keep?: DpsEntry[];
      foreign?: DpsEntry[];
      delete_plan?: boolean;
      entries?: DpsEntry[];
    }
  >;
  results: Record<string, { status: string; detail: string | null }> | null;
  window_start: string | null;
  window_end: string | null;
  created_by: string | null;
  approved_by: string | null;
  applied_by: string | null;
  applied_at: string | null;
  created_at: string | null;
  note: string | null;
}

export function getChangeSets(): Promise<{ items: ChangeSetRow[]; pending: number }> {
  return apiFetch("/api/topology/changes");
}

export function stageChangeSet(nodeId: number, domain: string): Promise<ChangeSetRow> {
  return apiFetch("/api/topology/changes/stage", {
    method: "POST",
    body: { node_id: nodeId, domain },
  });
}

/** Stage deletion of an unused domain-local plan (refused if in use). */
export function stagePlanDelete(
  nodeId: number,
  domain: string,
  planName: string,
): Promise<ChangeSetRow> {
  return apiFetch("/api/topology/changes/stage-delete", {
    method: "POST",
    body: { node_id: nodeId, domain, plan_name: planName },
  });
}

export function approveChangeSet(id: number, typedNames: string[]): Promise<ChangeSetRow> {
  return apiFetch(`/api/topology/changes/${id}/approve`, {
    method: "POST",
    body: { typed_names: typedNames },
  });
}

export function scheduleChangeSet(
  id: number,
  windowStart: string,
  windowEnd: string,
): Promise<ChangeSetRow> {
  return apiFetch(`/api/topology/changes/${id}/schedule`, {
    method: "POST",
    body: { window_start: windowStart, window_end: windowEnd },
  });
}

export function applyChangeSet(id: number): Promise<ChangeSetRow> {
  return apiFetch(`/api/topology/changes/${id}/apply`, { method: "POST" });
}

export function rollbackChangeSet(id: number): Promise<ChangeSetRow> {
  return apiFetch(`/api/topology/changes/${id}/rollback`, { method: "POST" });
}

export function cancelChangeSet(id: number): Promise<ChangeSetRow> {
  return apiFetch(`/api/topology/changes/${id}/cancel`, { method: "POST" });
}

// --- backup rollout (ADR 0029) -------------------------------------------

/** A node + tenant domain: where a backup account lives. */
export interface DomainTarget {
  node_id: number;
  domain: string;
}

/** Why a target was chosen; `undecidable` rows need an explicit choice. */
export type BackupReason = "designated" | "observed" | "only-domain" | "undecidable";

/** An unpaired extension with its suggested backup target. */
export interface BackupSuggestion {
  node_id: number;
  domain: string;
  ext: string;
  display_name: string | null;
  target: DomainTarget | null;
  reason: BackupReason;
  actionable: boolean;
}

export function getBackupSuggestions(): Promise<{
  items: BackupSuggestion[];
  total: number;
  actionable: number;
}> {
  return apiFetch("/api/sync/backup-suggestions");
}

/** One hunt group / attendant and its standby-clone status (ADR 0037). */
export interface ServiceStandbyItem {
  account: string;
  usertype: string;
  node_id: number;
  domain: string;
  standby_node_id: number | null;
  standby_domain: string | null;
  has_clone: boolean;
  actionable: boolean;
  reason: string | null;
  instruction?: string | null;
}

export function getServiceStandby(): Promise<{ items: ServiceStandbyItem[]; gaps: number }> {
  return apiFetch("/api/sync/service-standby");
}

/** One authorised backup account to create. */
export interface RolloutItem {
  node_id: number;
  domain: string;
  ext: string;
  backup_node_id: number;
  backup_domain: string;
}

/** Per-item outcome; shape differs between a dry run and a real run. */
export interface RolloutResult {
  ext: string;
  status?: "ok" | "skipped" | "error";
  action?: "create" | "skip";
  detail: string | null;
  reason?: string | null;
  account_created?: boolean;
  target?: string;
  pair_id?: number;
}

/**
 * Create backup accounts for the given extensions.
 *
 * `dryRun` defaults to true and the server defaults it too: this creates
 * real accounts on a production PBX, so the safe value must not depend on
 * the caller remembering to send it.
 */
export function runBackupRollout(
  items: RolloutItem[],
  dryRun = true,
): Promise<{ dry_run: boolean; items: RolloutResult[] }> {
  return apiFetch("/api/sync/backup-rollout", {
    method: "POST",
    body: { items, dry_run: dryRun },
  });
}

// --- pairs ---------------------------------------------------------------

export function getPairs(): Promise<{ items: ExtensionPairRow[]; total: number }> {
  return apiFetch("/api/sync/pairs");
}

export function checkPairDrift(): Promise<Record<string, number>> {
  return apiFetch("/api/sync/pairs/check-drift", { method: "POST" });
}

/** Apply a confirmed pair to both nodes (dial plan + VM consolidation). */
export function writePair(pairId: number): Promise<Record<string, unknown>> {
  return apiFetch(`/api/topology/pairs/${pairId}/write`, { method: "POST" });
}

// --- dial plans ----------------------------------------------------------

export function getRulesets(): Promise<{ items: RulesetRow[] }> {
  return apiFetch("/api/topology/dialplan/rulesets");
}

export function pushDialplan(): Promise<{ results: Array<Record<string, unknown>> }> {
  return apiFetch("/api/topology/dialplan/push", { method: "POST" });
}

// --- DIDs ----------------------------------------------------------------

export function getDids(): Promise<{ items: DidRow[] }> {
  return apiFetch("/api/sync/dids");
}

/**
 * Assign/move a DID to a pair (or unassign with `pairId = null`).
 * `carrierAck` must be true — the operator confirms the carrier-side step
 * (the §4D blocking instruction) before the platform records the move.
 */
export function assignDid(
  didId: number,
  pairId: number | null,
  carrierAck: boolean,
): Promise<{ id: number; number: string; pair_id: number | null; status: string }> {
  return apiFetch(`/api/topology/dids/${didId}/assign`, {
    method: "POST",
    body: { pair_id: pairId, carrier_ack: carrierAck },
  });
}

export function importDids(csv: string, dryRun: boolean): Promise<DidImportReport> {
  return apiFetch("/api/sync/dids/import", { method: "POST", body: { csv, dry_run: dryRun } });
}

/** URL for the DID CSV export (same-origin GET the cookie authorizes). */
export function didsExportUrl(): string {
  return "/api/sync/dids/export.csv";
}

// --- DID discovery (ADR 0035) --------------------------------------------

/** One (node, domain, account, field) sighting of a discovered number. */
export interface DidSighting {
  node_id: number;
  domain: string;
  account: string;
  field: string;
  raw: string;
}

/** One reconciliation finding from the DID discovery sweep. */
export interface DidFindingRow {
  id: number;
  kind: "unknown_number" | "missing_on_nodes" | "placement_mismatch";
  number: string;
  did_id: number | null;
  evidence: DidSighting[];
  instructions: string | null;
  status: "open" | "acknowledged";
  ack_by: string | null;
  ack_at: string | null;
}

export function getDidFindings(): Promise<{ items: DidFindingRow[] }> {
  return apiFetch("/api/sync/dids/findings");
}

export function runDidDiscovery(): Promise<Record<string, number>> {
  return apiFetch("/api/sync/dids/discovery/run", { method: "POST" });
}

/** Adopt an unknown number: creates the DID with verified fields only. */
export function adoptDidFinding(
  findingId: number,
): Promise<{ did_id: number; number: string; status: string }> {
  return apiFetch(`/api/sync/dids/findings/${findingId}/adopt`, { method: "POST" });
}

/** Confirm a finding's instructions were completed — `confirmed` must be true. */
export function acknowledgeDidFinding(
  findingId: number,
  confirmed: boolean,
): Promise<{ id: number; status: string }> {
  return apiFetch(`/api/sync/dids/findings/${findingId}/acknowledge`, {
    method: "POST",
    body: { confirmed },
  });
}

export function dismissDidFinding(findingId: number): Promise<{ id: number; status: string }> {
  return apiFetch(`/api/sync/dids/findings/${findingId}/dismiss`, { method: "POST" });
}

// --- DID failover readiness (ADR 0037) -----------------------------------

export interface FailoverReadinessItem {
  did_id: number;
  number: string;
  pair_id: number;
  backup: string;
  backup_node_id: number;
  on_primary: boolean;
  on_backup: boolean;
  backup_known: boolean;
  ready: boolean;
  instruction: string | null;
}

export function getFailoverReadiness(): Promise<{
  items: FailoverReadinessItem[];
  ready: boolean;
  gaps: number;
}> {
  return apiFetch("/api/sync/dids/failover-readiness");
}
